import axios from 'axios';
import { getIsApiPending } from '../../../selectors/travelUg/payloadCollection';
import { apiTransitionPayloadCollection } from '../../../actions/travelUg/payloadCollection';
import { getAccessToken } from '../../../selectors/travelUg/payments';
import { getCustomer, getTravelDetailsForm, getTravellers } from '../../../selectors/travelUg/travelDetails';
import {
    getAlternatePremiumCalculation,
    getCustomerIdValue,
    getPremiumCalculation,
    getQuoteForm,
} from '../../../selectors/travelUg/quote';
import { getAgentForm } from '../../../selectors/travelUg/travelAgentModal';
import { setPreventUserProceed, setTravelDetailsSubmittedValue } from '../../../actions/travelUg/travelDetails';
import { documentUploadChecksService } from './documentUploadChecks';
import { premiumCalculationService } from './premiumCalculation';
import { makeSubmission as makeUpdateQuoteSubmission } from './updateQuote';
import { collectLoginDetails } from '../../../actions/travelUg/auth';
import { setShowTaggingErrorModal, setTaggingErrorMessage } from '../../../actions/travelUg/quote';
import { navigateToTravelNextOfKin } from '../../../actions/travelUg/routing';

export const submitTravelDetailsService = async (store, next, action) => {
    let state = store.getState();

    if (getIsApiPending(state)) {
        return next(action);
    }

    store.dispatch(setShowTaggingErrorModal(false));
    store.dispatch(setTaggingErrorMessage(null));
    store.dispatch(apiTransitionPayloadCollection.pending());

    try {
        //check that all documents have an id
        await documentUploadChecksService(store, next, action);

        const additionalTravellers = await formatTravellers(state);
        let customer = getCustomer(state);

        //run aml checks for customer & additional travellers
        await runCustomerAndTravellerAmlChecks(state, store, additionalTravellers, customer);

        //re-calculate premium
        await premiumCalculationService(store, next, action);

        //update quote details
        state = store.getState();
        await makeUpdateQuoteSubmission(state);

        //submit travel details
        state = store.getState();
        customer = getCustomer(state);
        await makeSubmission(state, additionalTravellers, customer);

        store.dispatch(setTravelDetailsSubmittedValue(true));

        store.dispatch(navigateToTravelNextOfKin());
    } catch (error) {
        console.error(error);
        store.dispatch(setTravelDetailsSubmittedValue(false));

        const status = error?.response?.status;

        if (status === 401) {
            store.dispatch(collectLoginDetails());
        }

        store.dispatch(setTaggingErrorMessage('Something went wrong. Try again later'));
        store.dispatch(setShowTaggingErrorModal(true));
    } finally {
        store.dispatch(apiTransitionPayloadCollection.initial());
    }

    return next(action);
};

const runCustomerAndTravellerAmlChecks = async (state, store, additionalTravellers, customer) => {
    const includesBlacklistedTraveller = checkIfListIncludesBlacklistedTraveller(additionalTravellers);

    if (includesBlacklistedTraveller) {
        store.dispatch(setPreventUserProceed(true));
    } else {
        store.dispatch(setPreventUserProceed(false));
    }

    const amlResponse = await runAmlCheck(state, customer);

    if (amlResponse.data.message) {
        store.dispatch(setPreventUserProceed(true));
    } else {
        store.dispatch(setPreventUserProceed(false));
    }
};

const getFullName = (person) => {
    const firstName = person.firstName?.value || '';
    const middleName = person.middleName?.value || '';
    const lastName = person.lastName?.value || '';

    return [firstName, middleName, lastName].filter(Boolean).join(' ');
};

const formatTravellers = async (state) => {
    const travellers = getTravellers(state);

    const results = await Promise.allSettled(
        travellers.map(async (traveller) => {
            const resp = await runAmlCheck(state, {
                firstName: traveller.firstName,
                middleName: traveller.middleName,
                lastName: traveller.lastName,
            }).catch(() => null);

            return {
                fullName: getFullName(traveller),
                dateOfBirth: traveller.dob?.value,
                passportNumber: traveller.passportNumber?.value || 'N/A',
                documents: [{ document: traveller.passportDocumentId?.value }],
                blacklisted: resp?.data.message || false,
            };
        })
    );

    const formattedTravellers = results.map((result) => result.value);

    return formattedTravellers;
};

const getUserQuoteIdentification = (premiumCalc, alternatePremiumCalc, selectedCover) => {
    if (typeof selectedCover.value !== 'string') return '[]';
    return JSON.stringify(
        selectedCover.value.includes('Elite')
            ? alternatePremiumCalc.value?.quote_id || []
            : premiumCalc.value?.quote_id || []
    );
};

const checkIfListIncludesBlacklistedTraveller = (travellers) => {
    if (!Array.isArray(travellers) || travellers.length === 0) {
        return false;
    }

    return travellers.some((traveller) => traveller.blacklisted);
};

const buildDocumentArray = (...documentValues) => {
    return documentValues.filter((value) => value != null).map((value) => ({ document: value }));
};

const payloadCollection = (state, additionalTravellers, customer) => {
    const travelDetails = getTravelDetailsForm(state);
    const customerId = getCustomerIdValue(state);
    const premiumCalc = getPremiumCalculation(state);
    const alternatePremiumCalc = getAlternatePremiumCalculation(state);
    const quotation = getQuoteForm(state);
    const quoteIdentification = getUserQuoteIdentification(premiumCalc, alternatePremiumCalc, quotation.selectedCover);
    const fullName = getFullName(customer);

    const payload = {
        id: customerId.value,
        quoteIdentification,
        nationalityType: travelDetails.nationalityType.value || 'N/A',
        fullName,
        dateOfBirth: customer.dob.value || 'N/A',
        nationalIdNumber: travelDetails.idNumber.value || 'N/A',
        passportNumber: customer.passportNumber.value || 'N/A',
        taxIdentificationNumber: travelDetails.tinNumber.value || 'N/A',
        occupation: travelDetails.profession.value || 'N/A',
        phoneNumber: travelDetails.phoneNumber.value || 'N/A',
        officeNumber: travelDetails.officeNumber.value || 'N/A',
        email: customer.email.value || 'N/A',
        address: travelDetails.postalAddress.value || 'N/A',
        town: travelDetails.town.value || 'N/A',
        bankName: travelDetails.bankName.value || 'N/A',
        bankAccountName: travelDetails.bankHolderName.value || 'N/A',
        bankAccountNumber: travelDetails.bankAccountNumber.value || 'N/A',
        bankBranch: travelDetails.bankBranch.value || 'N/A',
        bankAccountCurrency: travelDetails.bankAccountCurrency.value || 'N/A',
        documents: buildDocumentArray(travelDetails.idDocumentId.value, customer.passportDocumentId.value),
        additionalTravellers,
        personalDataConsent: travelDetails.personalDataProcessingConsent.value || false,
        childDataConsent: travelDetails.childDataProcessingConsent.value,
        marketingConsent: travelDetails.marketingUseConsent.value,
    };

    return {
        ...payload,
    };
};

const makeSubmission = async (state, additionalTravellers, customer) => {
    const loginData = getAccessToken(state);

    try {
        const url = '/om-api/travel-ug/submit/travel-details';
        const payload = payloadCollection(state, additionalTravellers, customer);

        return await axios.post(url, payload, {
            headers: {
                'Content-Type': 'application/json',
                accessToken: loginData.accessToken,
            },
        });
    } catch (e) {
        console.error(e);
        throw e;
    }
};

const amlWhitelistCheckServicePayload = (state, details) => {
    const agent = getAgentForm(state);

    const amlWhitelistCalculationPayload = {
        firstName: details.firstName.value || '',
        surName: details.lastName.value || '',
        middleName: details.middleName.value || '',
        agentType: agent.intermediaryType?.value || '',
        coverType: 'Travel Insurance',
    };

    return {
        ...amlWhitelistCalculationPayload,
    };
};

const runAmlCheck = async (state, details) => {
    const loginData = getAccessToken(state);

    try {
        const url = '/om-api/ug-motor-insurance/aml-whitelist';

        return await axios.post(url, amlWhitelistCheckServicePayload(state, details), {
            headers: {
                'Content-Type': 'application/json',
                accessToken: loginData.accessToken,
            },
        });
    } catch (e) {
        console.error(e);
        throw e;
    }
};
